// =====================================================
// S-I18N-LANDING-REST — namespaceهایی که یک مسیر *پیش از اولین پِینت* لازم دارد.
//
// چرا لازم شد. `src/i18n/index.ts` فقط `common` + `shell` را قبل از رندر
// await می‌کند؛ بقیهٔ namespaceها تنبل‌اند و react-i18next با
// `useSuspense: false` تا رسیدنشان **خودِ کلید** را چاپ می‌کند. روی شبکهٔ
// سریع این پنجره چند میلی‌ثانیه است و هیچ تستی نمی‌بیندش، ولی روی
// Slow-3G یک رفت‌وبرگشت است و کاربر `home.platformArch.title` می‌خواند.
// اندازه‌گیری شد (پروژهٔ `slow-network`، کوکیِ ar، ۵۰۰ms تأخیر به‌ازای هر
// asset): `/` کلیدِ `home.topbar.registrationBadge` را چاپ می‌کرد،
// `/about` کلیدِ `about.hero.titleLine1` و `/programs` کلیدِ
// `programs.hero.titleLine1` — هر سه پیش از این بسته هم نشت داشتند.
//
// راهِ حل، و چرا این‌یکی و نه گاردِ `ready`. اگر بخشِ لندینگ را پشتِ
// `ready` پنهان کنیم، همان پنجره به‌جایِ کلیدِ خام یک صفحهٔ خالی می‌شود و
// LCPِ مهم‌ترین مسیرِ سایت خراب می‌شود. اینجا برعکس عمل می‌کنیم: کاتالوگِ
// همان مسیر را **هم‌زمان** با `common`/`shell` درخواست می‌کنیم. چون هر سه
// موازی می‌روند، هیچ رفت‌وبرگشتِ تازه‌ای اضافه نمی‌شود — و آن یک
// رفت‌وبرگشتِ سریالی که امروز *بعد از* mount شدنِ صفحه انجام می‌شد حذف
// می‌شود.
//
// فقط برایِ اولین پِینت است: ناوبریِ سمتِ کلاینت صفحه‌ای که همین حالا
// رنگ شده را نگه می‌دارد و react-i18next با رسیدنِ namespace دوباره رندر
// می‌کند.
// =====================================================

/**
 * پیشوندِ مسیر → namespaceهایی که همان اولین رندر به آن‌ها متن می‌دهد.
 * فقط مسیرهایی اینجا می‌آیند که نشتشان *اندازه‌گیری* شده باشد؛ فهرست را با
 * شاهد گسترده کنید، نه با حدس (هر ورودی یک chunk به مسیرِ بحرانی اضافه می‌کند).
 *
 * کلید یک سگمنت است (`market`) یا دو سگمنت (`market/admin`). حل‌کننده اول
 * دقیق‌ترین را می‌آزماید و بعد به یک‌سگمنتی برمی‌گردد — S-FIX-BUNDLE-4 / R2-021
 * این را لازم کرد: زیردرختِ عمومیِ `/market/*` نباید کاتالوگِ ۷۰کیلوبایتیِ
 * استودیو را بگیرد، ولی `/market/admin/*` و `/market/studio/*` بدونش کلیدِ خام
 * چاپ می‌کنند. دو سگمنت کافی است؛ سگمنتِ سوم همیشه شناسه یا تب است.
 */
const FIRST_PAINT_NAMESPACES: Readonly<Record<string, readonly string[]>> = {
  // `/` — خودِ لندینگ. `landing.brand` هم لازم است چون بازنویسیِ
  // per-deployment است و پیش از `landing` حل می‌شود.
  "": ["landing", "landing.brand"],
  home: ["landing", "landing.brand"],
  about: ["landing", "landing.brand"],
  programs: ["landing", "landing.brand"],
  pricing: ["landing", "landing.brand"],
  // The ar-mobile login reload trace captured login.hero.title/email.label
  // before identity arrived. Await that measured cold-route catalogue in
  // parallel with the shell; guest login behavior and other routes stay intact.
  // The active-language indicator reuses the existing profile interface label;
  // preload that real consumer too, never render a raw key on cold login.
  login: ["identity", "profile"],
  // S-FIX-SEC-BUNDLE-1 بند ۰ — یافتهٔ بازِ W6. `/honor-code` (HonorCodePage در
  // `pages/Academic.tsx`) تمامِ متنِ دیدنی‌اش را از `legal` می‌گیرد، نه از یک
  // namespaceِ `honorCode` — پیشنهادِ `["honorCode"]` در CONTINUATION_PLAN
  // اشتباه بود؛ چنین namespaceای وجود ندارد. `academic` هم عمداً اینجا نیست:
  // تنها مصرفش در آن صفحه `data-screen-label` است که متنِ دیدنی نیست و
  // ۱۵KB به مسیرِ بحرانی اضافه می‌کرد.
  "honor-code": ["legal"],
  // S-FIX-BUNDLE-4 / R2-012 — اندازه‌گیری‌شده، نه حدس. `tests/e2e/
  // slow-3g-bundles-ar.spec.js` روی پروژهٔ `slow-network` (۴۰۰kbps + ۵۰۰ms
  // به‌ازای هر asset) روی `/market/bundles` و روی بارگذاریِ سردِ ar از
  // `/market/bundles/:ref` کلیدِ خامِ `bundles.detail.backButton` را در تمامِ
  // فریم‌ها تا `networkidle` چاپ می‌کرد؛ با این ورودی صفر شد.
  // هزینه‌اش صفر است: `Marketplace.tsx` و ۲۲ فایلِ دیگرِ همین درخت همین حالا
  // `useTranslation("market")` دارند، پس این chunk روی هر مسیرِ `/market/*`
  // به‌هرحال گرفته می‌شود — این ورودی فقط آن رفت‌وبرگشتِ سریالیِ «بعد از
  // mount» را به یک درخواستِ موازی با `common`/`shell` تبدیل می‌کند.
  market: ["market"],
  // S-FIX-BUNDLE-4 / R2-016 — اندازه‌گیری‌شده، نه حدس. `tests/e2e/
  // slow-3g-live-lobby-ar.spec.js` روی پروژهٔ `slow-network` (۴۰۰kbps +
  // ۵۰۰ms به‌ازای هر asset) روی بارگذاریِ سردِ `/live-class/:id/lobby` در
  // هر دو زبان کلیدهای خامِ `lobby.help.title`، `lobby.meta.host` و
  // `preflight.secure.label` را در تمامِ فریم‌ها تا `networkidle` چاپ
  // می‌کرد؛ با این ورودی صفر شد. این بسته ~۲۳۰ رشتهٔ کلاسِ زنده را از
  // بدنهٔ کد به کاتالوگِ `live` برد، پس پنجرهٔ نمایشِ کلیدِ خام از «چند
  // رشتهٔ فرعی» به «کلِ صفحه» بزرگ شد و دیگر قابلِ چشم‌پوشی نبود.
  // هزینه‌اش صفر است: هر سه مسیرِ `/live-class/*` همین حالا
  // `useTranslation("live")` دارند، پس این chunk به‌هرحال گرفته می‌شود —
  // این ورودی فقط آن رفت‌وبرگشتِ سریالیِ «بعد از mount» را به یک درخواستِ
  // موازی با `common`/`shell` تبدیل می‌کند.
  "live-class": ["live"],
  // `/recordings` now reads all archive chrome from `live`; preload it on a
  // cold deep-link so the non-Suspense page never exposes raw keys.
  recordings: ["live"],
  // The hi exam trace loaded both catalogues but kept early fa lobby copy.
  // Await the measured cold-route namespace; store invalidation also covers
  // later client-side navigation without changing exam/consent behavior.
  exam: ["assess"],
  // The reviewed settings screenshot exposed the untranslated route segment.
  // Its exact existing category label lives in profile, not shell.
  "settings/notifications": ["profile"],
  // S-FIX-BUNDLE-4 / R2-021 — اندازه‌گیری‌شده، نه حدس. `tests/e2e/
  // slow-3g-studio-ar.spec.js` روی پروژهٔ `slow-network` (۴۰۰kbps + ۵۰۰ms
  // به‌ازای هر asset) روی بارگذاریِ سردِ ar از `/market/admin/storage` کلیدِ
  // خامِ `storage.title` را در ۲۹ فریم از ۳۰ فریمِ نمونه‌برداری‌شده تا
  // `networkidle` چاپ می‌کرد؛ با این ورودی صفر شد. همان مسیر با ورودیِ
  // یک‌سگمنتیِ `market` پایین درمان نمی‌شد، چون تمامِ متنِ دیدنیِ کنسولِ
  // ذخیره‌سازی از `studio` می‌آید نه از `market`.
  //
  // چرا دو سگمنت و نه افزودنِ `studio` به خودِ `market`: زیردرختِ `/market/*`
  // پرترافیک‌ترین مسیرِ عمومیِ سایت است (بازار، صفحهٔ دوره، سبد، پرداخت) و هیچ
  // رشته‌ای از `studio` نمی‌خواند؛ گذاشتنِ آن کاتالوگ روی مسیرِ بحرانیِ آن
  // صفحه‌ها ۷۰KB هزینهٔ بی‌مصرف بود.
  "market/admin": ["market", "studio"],
  // همان زیردرختِ نویسنده: پوستهٔ ۱۳تبیِ `/market/studio/:id/*` و جادویِ
  // ضبط/ویرایش هم تمامِ متنشان از `studio` می‌آید. `market` را هم نگه می‌داریم
  // چون `/market/studio/bundles` از `market` متن می‌گیرد و همین ورودی بر
  // ورودیِ یک‌سگمنتی سایه می‌اندازد.
  "market/studio": ["market", "studio"],
  // S-FIX-BUNDLE-4 / R2-115 — اندازه‌گیری‌شده، نه حدس. `tests/e2e/
  // slow-3g-grades-ar.spec.js` روی پروژهٔ `slow-network` (۴۰۰kbps + ۵۰۰ms
  // به‌ازای هر asset) روی بارگذاریِ سردِ ar کلیدِ خامِ `gradebook.mine.title` را
  // در `/my-grades` و `attendance.mine.title` را در `/my-attendance` تا
  // `networkidle` چاپ می‌کرد؛ با این دو ورودی صفر شد. هزینه‌اش صفر است: هر دو
  // صفحه همین حالا `useTranslation("academic")` دارند، پس این chunk به‌هرحال
  // گرفته می‌شود — این ورودی فقط آن رفت‌وبرگشتِ سریالیِ «بعد از mount» را به
  // درخواستی موازی با `common`/`shell` تبدیل می‌کند.
  //
  // بقیهٔ مسیرهایِ `academic` (کارنامهٔ استاد، حضورِ کلاس، فهرستِ کلاس) عمداً
  // اضافه نشد: اندازه‌گیری نشده‌اند و قاعدهٔ سرِ همین فایل می‌گوید ورودیِ حدسی
  // ننویس.
  "my-grades": ["academic"],
  "my-attendance": ["academic"],
  // S-FIX-BUNDLE-4 / R2-024 — اندازه‌گیری‌شده روی همان پروژهٔ `slow-network`.
  // `tests/e2e/slow-3g-wellness-ar.spec.js` روی بارگذاریِ سردِ ar کلیدِ خامِ
  // `tests.head.title` را در `/wellness/tests` در ۳۵ فریمِ متوالی (~۴٫۲ ثانیه)
  // و `results.head.title` را در `/wellness/results` در ۵۱ فریم (~۶٫۱ ثانیه)
  // چاپ می‌کرد — بدترین جایِ ممکن، چون بلوکِ اولِ `/wellness/tests` همان کفِ
  // ایمنیِ بحران است.
  //
  // چرا اینجا از خواهرش `/my-grades` بدتر بود: چانکِ عربیِ `wellness` ~۵۲.۶KB
  // است در برابرِ ~۲۹KB برایِ `academic`؛ روی ۴۰۰kbps همان اختلاف، پنجرهٔ نشت
  // را از یک پلک به چند ثانیه می‌کشاند. هزینهٔ این ورودی صفر است: هر دو صفحه
  // همین حالا `useTranslation("wellness")` دارند.
  //
  // عمداً دو کلیدِ دوسگمنتی و نه یک کلیدِ `wellness` یک‌سگمنتی: بقیهٔ زیردرخت
  // (`/wellness` داشبورد، `/wellness/test/:id`، `/wellness/result/:id`،
  // `/wellness/rich/:id`) اندازه‌گیری نشده و قاعدهٔ سرِ همین فایل ورودیِ حدسی
  // را ممنوع می‌کند. اندازه‌گیریِ آن‌ها کارِ بستهٔ بعد است.
  "wellness/tests": ["wellness"],
  "wellness/results": ["wellness"],
};

/** namespaceهای اضافیِ بوت برایِ `pathname` (خالی = فقط همان دو تایِ شل). */
export const firstPaintNamespaces = (pathname: string): readonly string[] => {
  const segments = pathname.replace(/^\/+/, "").split("/");
  const first = segments[0] ?? "";
  const second = segments[1] ?? "";
  // دقیق‌ترین اول: `market/admin` باید بر `market` سایه بیندازد. فقط وقتی
  // جفت را می‌آزماییم که هر دو سگمنت واقعاً وجود داشته باشند — کلیدِ خالی خودِ
  // ریشه (`/`) است و یک جفتِ ناقص نباید به آن برسد.
  if (first && second) {
    const pair = FIRST_PAINT_NAMESPACES[`${first}/${second}`];
    if (pair) return pair;
  }
  return FIRST_PAINT_NAMESPACES[first] ?? [];
};
